import { useTranslation } from 'react-i18next'
import { cn } from './cn'
import { formatSarAmount } from './money'

/** The Saudi Riyal sign (U+20C1), drawn by the Saudi Riyal webfont (public/fonts, OFL). */
export const RIYAL = '⃁'

/**
 * A price in Saudi riyals with the riyal sign. Arabic reads the amount then the sign ("29 ⃁"); English puts
 * the sign first ("⃁ 29"); either way the sign is drawn to the left of the figure. Latin digits, two decimals
 * only when needed. The sign is hidden from assistive tech, which reads the currency name instead.
 *
 *   <Sar amount={69} />                     ⃁ 69
 *   <Sar amount={55.2} suffix="/mo" />     with a trailing note
 *   <Sar amount={-589.96} />                −⃁ 589.96: a credit or refund. The minus (U+2212) leads the whole
 *                                           amount, sign included, in both directions, as on a Saudi tax
 *                                           invoice's credit lines; it never sits between the sign and figure.
 */
export function Sar({
  amount,
  className,
  signClassName,
  suffix,
}: {
  amount: number
  className?: string
  signClassName?: string
  suffix?: React.ReactNode
}) {
  const { t, i18n } = useTranslation('common')
  const ar = i18n.language === 'ar'
  // round first so -0.001 does not show as "−0"
  const negative = Math.round(amount * 100) < 0
  const sign = (
    <span aria-hidden="true" className={cn('[font-family:"Saudi_Riyal"] font-normal', signClassName)}>
      {RIYAL}
    </span>
  )
  const minus = negative ? <span aria-hidden="true">{'\u2212'}</span> : null
  const figure = (
    <bdi dir="ltr" className="figures">
      {negative ? <span className="sr-only">-</span> : null}
      {formatSarAmount(Math.abs(amount))}
    </bdi>
  )
  return (
    <span className={cn('inline-flex items-baseline gap-[0.18em] whitespace-nowrap', className)}>
      {/* an RTL row lays children out right to left: figure, sign, minus puts the minus leftmost */}
      {ar ? (
        <>
          {figure}
          {sign}
          {minus}
        </>
      ) : (
        <>
          {minus}
          {sign}
          {figure}
        </>
      )}
      <span className="sr-only"> {t('currency.sar')}</span>
      {suffix ? <span className="ms-[0.2em] text-[0.8em] font-normal text-subtle">{suffix}</span> : null}
    </span>
  )
}
